import {
  FormControl,
  FormLabel,
  IconButton,
  RadioGroup as MuiRadioGroup,
  SvgIcon,
  Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import { useController } from 'react-hook-form'

import InfoSmall from '@hapana/shared/web/styles/assets/svgs/info-small.svg'

import RadioButton from '../radio-button/radio-button.component'
import { RADIO_TYPES } from '../radio-button/radio-button.constants'
import Tooltip from '../tooltip/tooltip.component'

import { styles } from './radio-group.styles'

const RadioGroup = ({
  children,
  id,
  label,
  defaultValue,
  name,
  row,
  containerStyles,
  labelStyles,
  radioGroupStyles,
  options,
  radioType,
  tooltip,
  disabled,
  onChangeCallback,
  control,
  rules,
}) => {
  const {
    field: { value, onChange },
  } = useController({
    ...(control && { control }),
    ...(defaultValue !== undefined && { defaultValue }),
    name,
    rules,
  })

  const handleChange = (event) => {
    // Boolean values come as strings from event.target.value
    if (event.target.value === 'true') {
      onChange?.(true)
      onChangeCallback?.(true)
    } else if (event.target.value === 'false') {
      onChange?.(false)
      onChangeCallback?.(false)
    } else {
      onChange?.(event.target.value)
      onChangeCallback?.(event.target.value)
    }
  }

  return (
    <FormControl color="primary" sx={containerStyles}>
      <FormLabel sx={styles.labelContainer} id={id}>
        <Typography sx={[styles.label, labelStyles]}>{label}</Typography>
        {!!tooltip && (
          <Tooltip placement="top" title={tooltip}>
            <IconButton sx={styles.tooltip} disableRipple tabIndex={-1}>
              <SvgIcon component={InfoSmall} />
            </IconButton>
          </Tooltip>
        )}
      </FormLabel>
      <MuiRadioGroup
        sx={radioGroupStyles}
        row={row}
        aria-labelledby={id}
        defaultValue={defaultValue}
        value={value}
        name={name}
        onChange={handleChange}
        disabled={disabled}
      >
        {children ||
          options.map(({ label: optionLabel, helperText, value: radioButtonValue, sx }) => (
            <RadioButton
              test-id={`radio-group-${optionLabel}`}
              key={radioButtonValue}
              label={optionLabel}
              helperText={helperText}
              value={radioButtonValue}
              type={radioType ? radioType : RADIO_TYPES.BOX}
              disabled={disabled}
              sx={sx}
            />
          ))}
      </MuiRadioGroup>
    </FormControl>
  )
}

RadioGroup.propTypes = {
  children: PropTypes.node,
  value: PropTypes.string,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  name: PropTypes.string.isRequired,
  row: PropTypes.bool,
  onChange: PropTypes.func,
  onChangeCallback: PropTypes.func,
  containerStyles: PropTypes.object,
  labelStyles: PropTypes.object,
  radioGroupStyles: PropTypes.object,
  radioType: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      helperText: PropTypes.string,
      value: PropTypes.string,
      sx: PropTypes.object,
    })
  ),
  tooltip: PropTypes.string,
  disabled: PropTypes.bool,
  control: PropTypes.object,
  rules: PropTypes.object,
}

export default RadioGroup
